import type { MediaType } from '@/types'
import { uploadToBunny } from './bunny'

const CLOUD  = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME   as string
const PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string

const CHUNK_SIZE = 20 * 1024 * 1024

export interface CloudinaryResult {
  secure_url:        string
  public_id:         string
  resource_type:     string
  format:            string
  bytes:             number
  original_filename: string
  width?:            number
  height?:           number
  duration?:         number
  storage?:          'cloudinary' | 'bunny'
}

export function getMediaType(file: File): MediaType {
  if (file.type.startsWith('image/'))  return 'photo'
  if (file.type.startsWith('video/'))  return 'video'
  if (file.type.startsWith('audio/'))  return 'audio'
  return 'document'
}

// Subtype for documents — used to route to correct AI evaluation frame
export function getDocumentSubtype(file: File): 'pdf' | 'word' | 'text' | 'markdown' | 'other' {
  if (file.type === 'application/pdf')   return 'pdf'
  if (file.type === 'application/msword' ||
      file.type.includes('wordprocessingml')) return 'word'
  if (file.type === 'text/plain')         return 'text'
  if (file.type === 'text/markdown' || file.name.endsWith('.md')) return 'markdown'
  return 'other'
}

// Cloudinary resource_type — raw for documents, auto for media
export function getCloudinaryResourceType(file: File): 'auto' | 'raw' {
  const mt = getMediaType(file)
  if (mt === 'document') return 'raw'
  return 'auto'
}

export function uploadSimple(
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<CloudinaryResult> {
  return new Promise((resolve, reject) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('upload_preset', PRESET)
    fd.append('folder', 'the-room')
    fd.append('tags', `the-room,${new Date().getFullYear()}`)

    const resourceType = getCloudinaryResourceType(file)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD}/${resourceType}/upload`)

    xhr.upload.onprogress = e => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status === 200) resolve({ ...JSON.parse(xhr.responseText), storage: 'cloudinary' })
      else reject(new Error(`Cloudinary error ${xhr.status}: ${xhr.responseText}`))
    }
    xhr.onerror  = () => reject(new Error('Network error during upload'))
    xhr.onabort  = () => reject(new Error('Upload cancelled'))

    signal?.addEventListener('abort', () => xhr.abort())
    xhr.send(fd)
  })
}

export async function uploadChunked(
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<CloudinaryResult> {
  const resourceType = getCloudinaryResourceType(file)
  const url      = `https://api.cloudinary.com/v1_1/${CLOUD}/${resourceType}/upload`
  const uniqueId = `room-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const total    = file.size
  let   offset   = 0
  let   result: CloudinaryResult | null = null

  while (offset < total) {
    if (signal?.aborted) throw new Error('Upload cancelled')

    const end   = Math.min(offset + CHUNK_SIZE, total)
    const chunk = file.slice(offset, end)

    const fd = new FormData()
    fd.append('file', chunk, file.name)
    fd.append('upload_preset', PRESET)
    fd.append('folder', 'the-room')
    fd.append('tags', `the-room,${new Date().getFullYear()}`)

    const headers: Record<string, string> = {
      'X-Unique-Upload-Id': uniqueId,
      'Content-Range':      `bytes ${offset}-${end - 1}/${total}`,
    }

    let attempt = 0
    while (attempt < 3) {
      try {
        const res = await fetch(url, { method: 'POST', headers, body: fd, signal })
        if (!res.ok) throw new Error(`Chunk upload failed: ${res.status}`)
        const data = await res.json()
        if (end === total) result = { ...data, storage: 'cloudinary' }
        break
      } catch (err) {
        attempt++
        if (attempt === 3) throw err
        await sleep(1000 * attempt)
      }
    }

    offset = end
    onProgress(Math.round((offset / total) * 100))
  }

  if (!result) throw new Error('Chunked upload returned no result')
  return result
}

export async function uploadToCloudinary(
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<CloudinaryResult> {
  if (file.size > CHUNK_SIZE) {
    return uploadChunked(file, onProgress, signal)
  }
  return uploadSimple(file, onProgress, signal)
}

// ─── Dual upload with Bunny.net fallback ──────────────────────────────────────

export async function uploadWithFallback(
  file: File,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<CloudinaryResult> {
  try {
    const result = await uploadToCloudinary(file, onProgress, signal)
    console.log('[upload] Cloudinary ✓', result.public_id)
    return result
  } catch (cloudinaryErr) {
    // Don't fall back on user cancellation
    if (signal?.aborted) throw cloudinaryErr

    console.warn('[upload] Cloudinary failed, falling back to Bunny.net', cloudinaryErr)

    try {
      const bunnyResult = await uploadToBunny(file, onProgress, signal)
      console.log('[upload] Bunny.net fallback ✓', bunnyResult.public_id)
      return bunnyResult as CloudinaryResult
    } catch (bunnyErr) {
      // Both failed — throw original Cloudinary error with context
      console.error('[upload] Both Cloudinary and Bunny.net failed', bunnyErr)
      throw new Error(
        `Upload failed on both primary (Cloudinary) and fallback (Bunny.net). ` +
        `Primary error: ${cloudinaryErr instanceof Error ? cloudinaryErr.message : String(cloudinaryErr)}`
      )
    }
  }
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))
